import axios from 'axios';

const hostName = window.location.hostname;
const protocol = window.location.protocol;

// ---------------------------------------------------------------------------
// Runtime host resolution — white-label bc.<domain> pattern (2026-08-18).
//
// The dashboard/portal can be served from ANY domain: its API + socket
// host derives from the page's own hostname at RUNTIME, so attaching a
// new dashboard domain is DNS + a Dokploy mapping — no rebuild, no baked
// env. The backend pairs this with: dynamic HTTP CORS (origin https://X
// allowed when the request host is bc.X), the same rule on the socket
// handshake (socket.js allowRequest), and whitelistMiddleware
// auto-allowing bc.* hosts.
//
//   <domain>               → bc.<domain>         (www. stripped)
//   bc.<domain>            → same host           (legacy bc-served frontend)
//   legacy indiapayu hosts → bc.indiapayu.com    (historical mapping)
//   localhost              → localhost:3000      (dev backend)
//
// VUE_APP_API_URL / VUE_APP_SOCKET_URL are consulted ONLY for raw-IP
// hosts that no rule covers — never bake them for white-label builds.
// ---------------------------------------------------------------------------
const LEGACY_BC_HOSTS = ['indiapayu.com', 's.indiapayu.com', 'sbc.indiapayu.com'];

function resolveApiOrigin() {
  if (hostName === 'localhost' || hostName === '127.0.0.1') {
    return protocol + '//localhost:3000';
  }
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostName)) {
    // Raw IP — no bc.<ip> exists. Baked env (socket URL, or API URL with
    // its /api/v1 suffix stripped) or same-origin as last resort.
    const baked = process.env.VUE_APP_SOCKET_URL
      || (process.env.VUE_APP_API_URL || '').replace(/\/api\/v1\/?$/, '');
    return baked || window.location.origin;
  }
  if (LEGACY_BC_HOSTS.includes(hostName)) {
    return protocol + '//bc.indiapayu.com';
  }
  if (hostName.startsWith('bc.')) {
    return protocol + '//' + hostName;
  }
  return protocol + '//bc.' + hostName.replace(/^www\./, '');
}

const API_ORIGIN = resolveApiOrigin();
const baseURL = API_ORIGIN + '/api/v1';

// Socket endpoint origin — same host as the API; every io(...) call site
// imports this instead of the old baked VUE_APP_SOCKET_URL.
export const SOCKET_URL = API_ORIGIN;

// NO default timeout — deliberate product decision (24082026): operators
// legitimately run multi-minute searches (support team hunting old orders
// by UTR across wide windows). A global timeout was shipped and REVOKED
// same day; do not reintroduce one without explicit sign-off. The
// slot-starvation problem it targeted is addressed by request
// cancellation (see payin/actions.js getPayInList) and the pending
// socket-singleton work instead.
const http = axios.create({
  baseURL,
  headers: {
    'Content-type': 'application/json'
  },
  withCredentials: true
});

// Auth-storage keys we wipe when a 401 forces a client-side logout. Kept in
// sync with what auth/actions.js writes during login.
const AUTH_KEYS = [
  'token', 'id', 'username', 'role', 'vendor',
  'payInStatus', 'payOutStatus',
  'canApprovePayinManually',
  'canManageClients',
  'vendorDashboardEnabled',
  'isSupporting', 'permissions',
  'exp', 'iat', 'tokenExpiration',
];

let authFailureHandled = false;

// Skip the 401 redirect for endpoints where 401 is an expected non-fatal
// response (login attempt with bad password, logout itself, etc.). These
// match against the request URL.
const SKIP_401_REDIRECT = ['/login', '/client-login', '/logout'];

function clearLocalAuth() {
  for (const k of AUTH_KEYS) localStorage.removeItem(k);
  // Best-effort: clear the auth cookie too (httpOnly is false on this app).
  try {
    document.cookie = 'auth=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  } catch (e) { /* ignore */ }
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAuthEndpoint = SKIP_401_REDIRECT.some((p) => url.includes(p));

    if (status === 401 && !isAuthEndpoint && !authFailureHandled) {
      // Tomorrow-after-login symptom: the cookie / JWT has expired but
      // localStorage still says we're logged in. Wipe everything client-
      // side and bounce the user to login. The flag prevents redirect
      // loops if multiple in-flight requests all 401 at once.
      authFailureHandled = true;
      clearLocalAuth();
      // Use a hard reload so Vue, Vuex, and the router all re-bootstrap
      // from the now-empty localStorage. Hash router → '#/login'.
      window.location.replace(`${window.location.pathname}#/login`);
    }
    return Promise.reject(error);
  }
);

export default http;
